<template lang="pug">
.time-entries-for-date(v-if="show")
  .row
    .col-xs-8
      h4
        span.date
          | {{ formattedDatetime }}
        .d-inline-flex.align-items-center
          span.total_time
            data.duration
              | {{ $filters.formatDuration(totalDuration) }}
          span.overtime.d-flex.ml-5
            TimeEntriesOvertimePopover(
              :time-entries="timeEntries"
              :organization="organization"
              :show-financial="isOwner"
            )
        TimeEntriesCopyPreviewPopover(
          v-if="showCopyButton"
          :disabled="showTimeEntriesCopyRows"
        )
          template(#trigger)
            a.time-entries-copy-btn.btn.btn-icon.btn-naked.has-icon(
              :disabled="showTimeEntriesCopyRows"
              @click.stop="toggleTimeEntriesCopyRows"
            )
              i.hi.hi-clipboard.hi-16
    .col-xs-4
      a.btn.btn-icon.btn-naked.has-icon.pull-right.time-entries-view-notes-btn(
        v-if="hasNotes"
        @click="showNotesDialog"
      )
        i.hi.hi-16.hi-group-by
        span View notes
  TimeEntriesTimeline(
    v-if="showTimeline"
    :date="date"
    :time-entries="timeEntriesForTimeline"
    :click-to-add="clickToAdd"
  )
  .table-wrapper
    .scrollable
      table.has-actions.has-batch-actions.time-entries-table.has-actions.table
        thead(v-if="!timeEntriesIsEmpty || showTimeEntriesCopyRows")
          tr
            th
              AppCheckbox(
                :value="checked"
                :disabled="timeEntriesIsEmpty"
                @input="updateTimeEntriesToBatchDelete"
              )
            th.project-header {{ $filters.projectFlavourLabel(projectFlavour) }}
            th.reasons-header(v-if="tableColumns.show_reasons && reasonsOrNotesArePresent") Reason
            th(v-if="tableColumns.show_activity && showActivityColumns") Activity
            th(v-if="tableColumns.show_idle && showActivityColumns") Idle
            th Manual
            th(v-if="showTimeEntriesCopyRows")
              AppHintPopover
                template(#body)
                  p
                    | {{ copyPopoverText }}
                template(#trigger) Duration
            th(v-else) Duration
            th(v-if="showSourceColumn") Source
            th Time
            th(v-if="showScreenshotsColumn") Screenshots
            th
        tbody(v-if="timeEntriesIsEmpty && showTimeEntriesCopyRows")
          TimeEntriesCopyRow(
            v-for="(timeEntry, index) in skeletonsForDate"
            :key="index"
            :time-entry-skeleton="timeEntry"
            :time-entries="inPlaceTimeEntries"
            :reasons-or-notes-are-present="reasonsOrNotesArePresent"
            @add-time-entry="addToInPlaceTimeEntries"
          )
        tbody(v-else)
          TimeEntriesRow(
            v-for="timeEntry in timeEntries"
            :key="timeEntry.id"
            :time-entry="timeEntry"
            :time-entries="inPlaceTimeEntries"
            :checked="inTimeEntriesBatchDelete(timeEntry)"
            :reasons-or-notes-are-present="reasonsOrNotesArePresent"
          )
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { isEmpty, forEach, find, some } from 'lodash-es';
import { formatDate, formatTime } from '~/utils/time_utils';
import { getProjectFlavourLabel } from '~/utils/project_flavour';

export default {
  name: 'TimeEntriesForDate',
  props: {
    date: Object,
    timeEntries: Array,
    showTimeline: {
      type: Boolean,
      default: true,
    },
    showEmpty: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      showTimeEntriesCopyRows: false,
      inPlaceTimeEntries: [],
    };
  },
  computed: {
    ...mapState({ isOwner: 'is_owner' }),
    ...mapGetters([
      'user',
      'organization',
      'sumDuration',
      'hasFeatureKeyboardAndMouseActivity',
      'timeEntriesToBatchDelete',
      'isToday',
      'tableColumns',
      'addTimeDisabled',
      'ownActivity',
      'projectTasks',
      'timeEntrySkeletons',
      'skeletonsIsEmpty',
      'lockedRangeFor',
      'anyNoteForTimeEntry',
      'projectFlavour',
      'showSourceColumn',
      'isDeletable',
      'showScreenshotsColumn',
      'reasonsOrNotesArePresent',
    ]),
    show() {
      if (this.showEmpty) {
        return true;
      }

      return this.timeEntries.length > 0;
    },
    showCopyButton() {
      return (
        this.timeEntriesIsEmpty &&
        this.inPlaceTimeEntriesIsEmpty &&
        !this.dateIsTimesheetLocked &&
        !this.addTimeDisabled &&
        !this.skeletonsIsEmpty
      );
    },
    dateIsTimesheetLocked() {
      return this.lockedRange.start && this.lockedRange.stop;
    },
    lockedRange() {
      return this.lockedRangeFor(this.date);
    },
    timeEntriesForTimeline() {
      return this.inPlaceTimeEntriesIsEmpty ? this.timeEntries : this.inPlaceTimeEntries;
    },
    hasNotes() {
      return some(this.timeEntries, (timeEntry) => this.anyNoteForTimeEntry(timeEntry));
    },
    formattedDatetime() {
      return this.isToday(this.date) ? 'Today:' : `${formatDate(this.date)}:`;
    },
    totalDuration() {
      let timeEntries = [];

      if (!this.timeEntriesIsEmpty) {
        timeEntries = this.timeEntries;
      } else if (!this.inPlaceTimeEntriesIsEmpty) {
        timeEntries = this.inPlaceTimeEntries;
      }

      return this.sumDuration(timeEntries);
    },
    showActivityColumns() {
      return !this.organization || this.hasFeatureKeyboardAndMouseActivity;
    },
    inPlaceTimeEntriesIsEmpty() {
      return isEmpty(this.inPlaceTimeEntries);
    },
    clickToAdd() {
      return !(this.addTimeDisabled || this.dateIsTimesheetLocked);
    },
    timeEntriesIsEmpty() {
      return isEmpty(this.timeEntries);
    },
    skeletonsForDate() {
      return this.timeEntrySkeletons.map((timeEntry) => {
        const startTime = this.date.clone().hours(8).minutes(0).seconds(0).milliseconds(0);
        const stopTime = startTime.clone().add(1, 'hours');

        return Object.assign({}, timeEntry, {
          start_time: startTime,
          stop_time: stopTime,
        });
      });
    },
    checked() {
      return some(this.timeEntries, (timeEntry) => {
        return this.inTimeEntriesBatchDelete(timeEntry);
      });
    },
    copyPopoverText() {
      const label = getProjectFlavourLabel(this.projectFlavour);

      return `Add time to each ${label.toLowerCase()} to save entry`;
    },
  },
  methods: {
    addToInPlaceTimeEntries(timeEntry) {
      this.$store.dispatch(
        'addToInPlaceTimeEntries',
        Object.assign({}, timeEntry, { overall_tracked: timeEntry.duration.asSeconds() }),
      );
      this.inPlaceTimeEntries.push(timeEntry);
    },
    inTimeEntriesBatchDelete(timeEntry) {
      return !!find(this.timeEntriesToBatchDelete, { id: timeEntry.id });
    },
    updateTimeEntriesToBatchDelete(checkedBoolean) {
      forEach(this.timeEntries, (timeEntry) => {
        if (!this.isDeletable(timeEntry)) {
          return;
        }
        const action = checkedBoolean ? 'addToTimeEntriesBatchDelete' : 'removeFromTimeEntriesBatchDelete';

        this.$store.dispatch(action, timeEntry);
      });
    },
    toggleTimeEntriesCopyRows() {
      if (!this.showTimeEntriesCopyRows) {
        this.showTimeEntriesCopyRows = true;
        if (!isEmpty(this.lockedRange)) {
          let verbiage;
          const start = this.lockedRange.start;
          const stop = this.lockedRange.stop;

          if (stop) {
            verbiage = `Only time after ${formatTime(stop, {})}`;
          } else {
            verbiage = `Only time before ${formatTime(start, {})}`;
          }
          this.$appMessage(`${verbiage} can be added because of a locked timesheet`);
        }
        if (this.timeEntrySkeletons.length < this.projectTasks.length) {
          const verbiage = this.ownActivity ? 'you have been' : `${this.user.name} has been`;

          this.$appMessage(
            `Some projects and tasks could not be copied because they’ve been archived, completed, or ${verbiage} removed from them`,
          );
        }
      }
    },
    showNotesDialog() {
      this.$emit('show-notes-dialog', { timeEntries: this.timeEntries, date: this.date });
    },
  },
};
</script>
